import BigNumber from "bignumber.js";

/**
 * Formats a decimal amount for display: thousands separators, at most maxDecimals,
 * no trailing zeros, and "<0.000001" for values too small to show
 * Input fields keep the raw value, this is only for text
 */
export const formatAmount = (value, { maxDecimals = 6 } = {}) => {
  const amount = new BigNumber(value);
  if (!amount.isFinite()) return "-";
  if (amount.isZero()) return "0";
  // token data read from a contract carries decimals as a string ("8"), BigNumber needs a number
  const decimals = Number(maxDecimals);
  const smallest = new BigNumber(1).shiftedBy(-decimals);
  if (amount.abs().isLessThan(smallest)) {
    return `${amount.isNegative() ? ">-" : "<"}${smallest.toFormat()}`;
  }
  return amount.decimalPlaces(decimals, BigNumber.ROUND_DOWN).toFormat();
};

/**
 * Formats a raw token amount (no decimals, as stored on-chain)
 */
export const formatTokenAmount = (raw, decimals, options) =>
  formatAmount(new BigNumber(raw).shiftedBy(-Number(decimals || 0)), options);

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/**
 * USD value for tables and stats: "$41.2K", "$950.40", "<$0.01"; "—" without a value
 */
export const formatUSD = (value) => {
  if (value === null || value === undefined) return "—";
  const amount = new BigNumber(value);
  if (!amount.isFinite()) return "—";
  if (amount.isZero()) return "$0";
  if (amount.isLessThan(0.01)) return "<$0.01";
  if (amount.isLessThan(1000)) return `$${amount.toFormat(2, BigNumber.ROUND_HALF_UP)}`;
  return `$${compact.format(amount.toNumber())}`;
};

/**
 * Token price in USD: two decimals from $1, four significant digits below ("$0.0002466"), "<$0.00000001" for
 * dust prices; "—" without a price
 */
export const formatPriceUSD = (value) => {
  if (value === null || value === undefined) return "—";
  const price = new BigNumber(value);
  if (!price.isFinite()) return "—";
  if (price.isZero()) return "$0";
  if (price.isGreaterThanOrEqualTo(1)) return `$${price.toFormat(2, BigNumber.ROUND_HALF_UP)}`;
  if (price.isLessThan("0.00000001")) return "<$0.00000001";
  return `$${price.precision(4, BigNumber.ROUND_HALF_UP).toFixed()}`;
};

/**
 * Counts: "5,004" up to 9,999, then compact ("546.7K")
 */
export const formatCount = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const count = Number(value);
  return count < 10000 ? count.toLocaleString("en-US") : compact.format(count);
};
